import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet, Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "./context/AuthContext";

// Public Pages
import HomePage from "./pages/HomePage";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPasswordConfirm from "./pages/ResetPasswordConfirm";
import FeaturesPage from "./pages/FeaturesPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import SetPassword from "./pages/SetPassword";

// Private Pages / Components
import FrameworkPage from "./pages/FrameworkPage";
import COBITInstructions from "./components/COBITInstructions";
import BCMInstructions from "./components/BCMInstructions";
import COBITAssessmentDashboardPage from "./pages/COBITAssessmentDashboardPage";
import BCMAssessmentDashboardPage from "./pages/BCMAssessmentDashboardPage";
import COBITAssessmentPage from "./components/cobit/COBITAssessmentPage";
import BCMAssessmentPage from "./components/bcm/BCMAssessmentPage";
import COBITReport from "./components/COBITReport";
import BCMReport from "./components/BCMReport";
import ProtectedPrivacyRoute from "./components/ProtectedPrivacyRoute";
import PrivacyAssessmentNotice from "./pages/PrivacyAssessmentNotice";

import Layout from "./components/Layout";

// ---------------------------
// Loading Screen
// ---------------------------
const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
      <p className="text-amber-700 font-medium">Loading...</p>
    </div>
  </div>
);

// ---------------------------
// PrivateRoute using AuthContext
// ---------------------------
const PrivateRoute = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <Outlet />;
};

// ---------------------------
// PublicRoute - Redirect authenticated users away from auth pages
// ---------------------------
const PublicRoute = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user && (location.pathname === "/login" || location.pathname === "/forgot-password")) {
    return <Navigate to="/frameworks" replace />;
  }

  return <Outlet />;
};

// ---------------------------
// 404 Page
// ---------------------------
const NotFound = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-6">
    <div className="text-center max-w-md">
      <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
      <p className="text-xl text-gray-600 mb-8">
        Oops! The page you're looking for doesn't exist.
      </p>
      <Link
        to="/"
        className="inline-block px-8 py-3 bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
      >
        Go Home
      </Link>
    </div>
  </div>
);

function App() {
  return (
    <Router>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Routes>
        {/* Public Routes */}
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Route>
          <Route path="/reset-password/:uid/:token" element={<ResetPasswordConfirm />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Route>

        {/* Protected Routes */}
        <Route element={<PrivateRoute />}>
          <Route element={<Layout />}>

            {/* Accessible after login only */}
            <Route path="/frameworks" element={<FrameworkPage />} />
            <Route path="/cobit/instructions" element={<COBITInstructions />} />
            <Route path="/bcm/instructions" element={<BCMInstructions />} />
            <Route path="/privacy-notice" element={<PrivacyAssessmentNotice />} />

            {/* Privacy-protected routes */}
            <Route element={<ProtectedPrivacyRoute />}>

              <Route
                path="/cobit/assessment"
                element={<COBITAssessmentPage />}
              />

              <Route
                path="/bcm/assessment"
                element={<BCMAssessmentPage />}
              />

              <Route
                path="/cobit/dashboard"
                element={<COBITAssessmentDashboardPage type="cobit" />}
              />

              <Route
                path="/bcm/dashboard"
                element={<BCMAssessmentDashboardPage type="bcm" />}
              />

              <Route
                path="/cobit/report"
                element={<COBITReport type="cobit" />}
              />

              <Route
                path="/bcm/report"
                element={<BCMReport type="bcm" />}
              />

            </Route>

          </Route>
        </Route>

        {/* Catch-all 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;